import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { GatewayOutage } from "@/contracts";
import { IconCircleCheck, IconUserCheck, IconWifiOff } from "@tabler/icons-react";

import { gatewayOutagesQuery } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { SelectField } from "@/components/ui/field";
import { ErrorBlock, LoadingBlock } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { useApiClient } from "@/lib/api-context";
import { formatDateTime, formatDevEui, formatRelative } from "@/lib/format";
import { AcknowledgeOutageDialog } from "./acknowledge-outage-dialog";

const COLUMNS = 7;

/**
 * Gateways that stayed disconnected past their alert window (ADR-093).
 *
 * Super admin only: they are the ones emailed, and a shared gateway's status
 * is not something a tenant is shown (ADR-062).
 */
export function GatewayOutagesCard() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const [open, setOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [acknowledging, setAcknowledging] = useState<GatewayOutage | null>(null);

  const outages = useQuery(gatewayOutagesQuery(client, open, page));

  return (
    <Card className="mb-4">
      <CardHeader
        title={t("gatewayOutagesTitle")}
        description={t("gatewayOutagesDescription")}
        action={
          <div className="w-48">
            <SelectField
              label={tCommon("show")}
              value={open ? "open" : "all"}
              onChange={(value) => {
                setOpen(value !== "all");
                setPage(1);
              }}
              data={[
                { value: "open", label: t("gatewayOutagesViewOpen") },
                { value: "all", label: t("gatewayOutagesViewAll") },
              ]}
            />
          </div>
        }
      />

      {outages.isPending ? <LoadingBlock label={t("loadingGatewayOutages")} /> : null}

      {outages.isError ? (
        <ErrorBlock error={outages.error} onRetry={() => void outages.refetch()} />
      ) : null}

      {outages.data ? (
        <DataTable caption={t("gatewayOutagesTitle")}>
          <thead>
            <tr>
              <Th>{t("colGateway")}</Th>
              <Th>{tCommon("organization")}</Th>
              <Th>{t("colOutageSince")}</Th>
              <Th>{t("colLastHeard")}</Th>
              <Th numeric>{t("colDevices")}</Th>
              <Th>{tCommon("status")}</Th>
              <Th>
                <span className="sr-only">{t("colActions")}</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {outages.data.data.length === 0 ? (
              <EmptyRow colSpan={COLUMNS}>
                {open ? t("emptyGatewayOutagesOpen") : t("emptyGatewayOutages")}
              </EmptyRow>
            ) : null}

            {outages.data.data.map((outage) => (
              <tr key={outage.id}>
                <Td>
                  <span className="font-medium">{outage.gatewayName}</span>
                  <span className="block font-mono text-xs text-ink-muted">
                    {formatDevEui(outage.gatewayEui)}
                  </span>
                </Td>
                <Td>{outage.organizationName ?? tCommon("sharedInfrastructure")}</Td>
                <Td>
                  <span title={formatDateTime(outage.startedAt)}>
                    {formatRelative(outage.startedAt)}
                  </span>
                </Td>
                <Td>{formatRelative(outage.lastSeenAt)}</Td>
                <Td numeric>{outage.deviceCount}</Td>
                <Td>
                  <OutageStatus outage={outage} />
                </Td>
                <Td>
                  {outage.resolvedAt === null && outage.acknowledgedAt === null ? (
                    <Button variant="secondary" size="sm" onClick={() => setAcknowledging(outage)}>
                      {t("acknowledge")}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <Td colSpan={COLUMNS} className="border-b-0">
                <Pagination
                  page={outages.data.pagination.page}
                  totalPages={outages.data.pagination.totalPages}
                  total={outages.data.pagination.total}
                  noun={{ one: t("outageNoun.one"), other: t("outageNoun.other") }}
                  onChange={setPage}
                />
              </Td>
            </tr>
          </tfoot>
        </DataTable>
      ) : null}

      {acknowledging ? (
        <AcknowledgeOutageDialog outage={acknowledging} onClose={() => setAcknowledging(null)} />
      ) : null}
    </Card>
  );
}

function OutageStatus({ outage }: { outage: GatewayOutage }) {
  const { t } = useTranslation("pages");

  if (outage.resolvedAt) {
    return (
      <>
        <Badge tone="ok" icon={IconCircleCheck}>
          {t("outageStatusResolved")}
        </Badge>
        <span
          className="mt-1 block text-xs text-ink-muted"
          title={formatDateTime(outage.resolvedAt)}
        >
          {formatRelative(outage.resolvedAt)}
        </span>
      </>
    );
  }

  if (outage.acknowledgedAt) {
    return (
      <>
        <Badge tone="warn" icon={IconUserCheck}>
          {t("outageStatusAcknowledged")}
        </Badge>
        {outage.acknowledgedByName ? (
          <span className="mt-1 block text-xs text-ink-muted">
            {t("acknowledgedBy", { name: outage.acknowledgedByName })}
          </span>
        ) : null}
      </>
    );
  }

  return (
    <Badge tone="danger" icon={IconWifiOff}>
      {t("outageStatusOpen")}
    </Badge>
  );
}
